import { Box, Flex, FlexProps, mdBumps, smBumps, Typo } from '@wrisk/ui-components'
import React, { FunctionComponent } from 'react'

import { Quote } from '../../../domain'
import { useCoverLevelExcessConfig, useExcesses } from '../../../hooks/excess'
import {
  tExcesses,
  TKey,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import { allEqual } from '../../../util/array'
import { ExcessEndorsements } from './ExcessEndorsements'
import { ExcessRow } from './ExcessRow'

export interface ExcessSummaryProps extends FlexProps {
  quote: Quote
}

const tKey = TKey('components.excess-summary')

export const ExcessSummary: FunctionComponent<ExcessSummaryProps> = ({
  quote,
  ...props
}) => {
  const excessConfig = useCoverLevelExcessConfig()

  return (
    <Flex
      width={1}
      flexDirection='column'
      justifyContent='flex-start'
      rowGap={smBumps}
      py={smBumps}
      {...props}
    >
      {excessConfig ? <ApplicableExcesses quote={quote} /> : <Excesses quote={quote} />}
      <ExcessEndorsements quote={quote} />
    </Flex>
  )
}

const Excesses: FunctionComponent<ExcessSummaryProps> = ({ quote }) => {
  const { t } = useWriskTranslation()

  const { main, other, voluntary } = useExcesses(quote)

  const mainExcessValues = main.map((it) => it.mandatoryExcess)

  const isRolledUp = allEqual(mainExcessValues)

  const mainExcessSection = main.length ? (
    isRolledUp ? (
      <ExcessRow
        currentExcess={mainExcessValues[0]}
        header={t(tKey('headings.compulsory'))}
        typoSize='sm'
        px={smBumps}
      />
    ) : (
      <>
        <Box px={mdBumps} py={2}>
          <Typo typoSize='sm'>{t(tKey('headings.compulsory'))}</Typo>
        </Box>
        {main.map((it) => (
          <ExcessRow
            key={it.excessCategory}
            currentExcess={it.mandatoryExcess}
            header={t(tExcesses(it.excessCategory, 'name'))}
            typoSize='sm'
            px={smBumps}
          />
        ))}
      </>
    )
  ) : null

  // TODO (MK): Doesn't take which voluntary excess into account
  const voluntaryExcessSection = voluntary.map((it) => (
    <ExcessRow
      key={`voluntary-${it.voluntaryExcessCode}`}
      currentExcess={it.excess}
      header={t(tKey('headings.voluntary'))}
      typoSize='sm'
      px={smBumps}
    />
  ))

  const otherExcessSection = other.map((it) => (
    <ExcessRow
      key={it.excessCategory}
      currentExcess={it.mandatoryExcess}
      header={t(tExcesses(it.excessCategory, 'name'))}
      typoSize='sm'
      px={smBumps}
    />
  ))

  return (
    <>
      {mainExcessSection}
      {voluntaryExcessSection}
      {otherExcessSection}
    </>
  )
}

const ApplicableExcesses: FunctionComponent<ExcessSummaryProps> = ({ quote }) => {
  const { t } = useWriskTranslation()

  const { applicable } = useExcesses(quote)

  return applicable.map((it) => (
    <ExcessRow
      key={it.excessCategory}
      currentExcess={it.totalExcess}
      header={t(tExcesses(it.excessCategory, 'name'))}
      typoSize='sm'
      px={smBumps}
    />
  ))
}
